import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddPoolMemberParams,
  AddPoolMemberResponse,
  CreateHealthMonitorParams,
  CreateHealthMonitorResponse,
  CreateL7PolicyParams,
  CreateL7PolicyResponse,
  CreateL7RuleParams,
  CreateL7RuleResponse,
  CreateListenerParams,
  CreateListenerResponse,
  CreateLoadBalancerParams,
  CreateLoadBalancerResponse,
  CreatePoolParams,
  CreatePoolResponse,
  DeleteHealthMonitorParams,
  DeleteL7PolicyParams,
  DeleteL7RuleParams,
  DeleteListenerParams,
  DeleteLoadBalancerParams,
  DeletePoolParams,
  GetHealthMonitorDetailsParams,
  GetHealthMonitorDetailsResponse,
  GetHealthMonitorsListParams,
  GetHealthMonitorsListResponse,
  GetL7PoliciesListParams,
  GetL7PoliciesListResponse,
  GetL7PolicyDetailsParams,
  GetL7PolicyDetailsResponse,
  GetL7RuleDetailsParams,
  GetL7RuleDetailsResponse,
  GetL7RulesListParams,
  GetL7RulesListResponse,
  GetListenerDetailsParams,
  GetListenerDetailsResponse,
  GetListenersListParams,
  GetListenersListResponse,
  GetLoadBalancerDetailsParams,
  GetLoadBalancerDetailsResponse,
  GetLoadBalancersListParams,
  GetLoadBalancersListResponse,
  GetPoolDetailsParams,
  GetPoolDetailsResponse,
  GetPoolMemberDetailsParams,
  GetPoolMemberDetailsResponse,
  GetPoolMembersListParams,
  GetPoolMembersListResponse,
  GetPoolsListParams,
  GetPoolsListResponse,
  HealthMonitor,
  L7Policy,
  L7Rule,
  Listener,
  LoadBalancer,
  Pool,
  PoolMember,
  RemovePoolMemberParams,
  UpdateHealthMonitorParams,
  UpdateHealthMonitorResponse,
  UpdateL7PolicyParams,
  UpdateL7PolicyResponse,
  UpdateL7RuleParams,
  UpdateL7RuleResponse,
  UpdateListenerParams,
  UpdateListenerResponse,
  UpdateLoadBalancerParams,
  UpdateLoadBalancerResponse,
  UpdatePoolMemberParams,
  UpdatePoolMemberResponse,
  UpdatePoolParams,
  UpdatePoolResponse
} from "./types";

export type LoadBalancersState = {
  loadBalancers: LoadBalancer[] | null;
  areLoadBalancersLoading: boolean;
  loadBalancer: LoadBalancer | null;
  isLoadBalancerLoading: boolean;
  isLoadBalancerCreating: boolean;
  isLoadBalancerUpdating: boolean;
  isLoadBalancerDeleting: boolean;

  listeners: Listener[] | null;
  areListenersLoading: boolean;
  listener: Listener | null;
  isListenerLoading: boolean;
  isListenerCreating: boolean;
  isListenerUpdating: boolean;
  isListenerDeleting: boolean;

  pools: Pool[] | null;
  arePoolsLoading: boolean;
  pool: Pool | null;
  isPoolLoading: boolean;
  isPoolCreating: boolean;
  isPoolUpdating: boolean;
  isPoolDeleting: boolean;

  poolMembers: PoolMember[] | null;
  arePoolMembersLoading: boolean;
  poolMember: PoolMember | null;
  isPoolMemberLoading: boolean;
  isPoolMemberAdding: boolean;
  isPoolMemberUpdating: boolean;
  isPoolMemberRemoving: boolean;

  l7Policies: L7Policy[] | null;
  areL7PoliciesLoading: boolean;
  l7Policy: L7Policy | null;
  isL7PolicyLoading: boolean;
  isL7PolicyCreating: boolean;
  isL7PolicyUpdating: boolean;
  isL7PolicyDeleting: boolean;

  l7PolicyRules: L7Rule[] | null;
  areL7PolicyRulesLoading: boolean;
  l7PolicyRule: L7Rule | null;
  isL7PolicyRuleLoading: boolean;
  isL7PolicyRuleCreating: boolean;
  isL7PolicyRuleUpdating: boolean;
  isL7PolicyRuleDeleting: boolean;

  healthMonitors: HealthMonitor[] | null;
  areHealthMonitorsLoading: boolean;
  healthMonitor: HealthMonitor | null;
  isHealthMonitorLoading: boolean;
  isHealthMonitorCreating: boolean;
  isHealthMonitorUpdating: boolean;
  isHealthMonitorDeleting: boolean;
};

const initialState: LoadBalancersState = {
  loadBalancers: null,
  areLoadBalancersLoading: false,
  loadBalancer: null,
  isLoadBalancerLoading: false,
  isLoadBalancerCreating: false,
  isLoadBalancerUpdating: false,
  isLoadBalancerDeleting: false,

  listeners: null,
  areListenersLoading: false,
  listener: null,
  isListenerLoading: false,
  isListenerCreating: false,
  isListenerUpdating: false,
  isListenerDeleting: false,

  pools: null,
  arePoolsLoading: false,
  pool: null,
  isPoolLoading: false,
  isPoolCreating: false,
  isPoolUpdating: false,
  isPoolDeleting: false,

  poolMembers: null,
  arePoolMembersLoading: false,
  poolMember: null,
  isPoolMemberLoading: false,
  isPoolMemberAdding: false,
  isPoolMemberUpdating: false,
  isPoolMemberRemoving: false,

  l7Policies: null,
  areL7PoliciesLoading: false,
  l7Policy: null,
  isL7PolicyLoading: false,
  isL7PolicyCreating: false,
  isL7PolicyUpdating: false,
  isL7PolicyDeleting: false,

  l7PolicyRules: null,
  areL7PolicyRulesLoading: false,
  l7PolicyRule: null,
  isL7PolicyRuleLoading: false,
  isL7PolicyRuleCreating: false,
  isL7PolicyRuleUpdating: false,
  isL7PolicyRuleDeleting: false,

  healthMonitors: null,
  areHealthMonitorsLoading: false,
  healthMonitor: null,
  isHealthMonitorLoading: false,
  isHealthMonitorCreating: false,
  isHealthMonitorUpdating: false,
  isHealthMonitorDeleting: false
};

const loadBalancersSlice = createSlice({
  name: "loadBalancers",
  initialState,
  reducers: {
    // Load Balancers
    listLoadBalancersRequest: (
      state,
      action: PayloadAction<GetLoadBalancersListParams>
    ) => {
      state.areLoadBalancersLoading = true;
    },
    listLoadBalancersSuccess: (
      state,
      action: PayloadAction<GetLoadBalancersListResponse>
    ) => {
      state.loadBalancers = action.payload;
      state.areLoadBalancersLoading = false;
    },
    listLoadBalancersFailure: (
      state,
      action: PayloadAction<{
        params: GetLoadBalancersListParams;
        error: string;
      }>
    ) => {
      state.areLoadBalancersLoading = false;
    },

    getLoadBalancerRequest: (
      state,
      action: PayloadAction<GetLoadBalancerDetailsParams>
    ) => {
      state.isLoadBalancerLoading = true;
    },
    getLoadBalancerSuccess: (
      state,
      action: PayloadAction<GetLoadBalancerDetailsResponse>
    ) => {
      state.loadBalancer = action.payload;
      state.isLoadBalancerLoading = false;
    },
    getLoadBalancerFailure: (
      state,
      action: PayloadAction<{
        params: GetLoadBalancerDetailsParams;
        error: string;
      }>
    ) => {
      state.isLoadBalancerLoading = false;
    },

    createLoadBalancerRequest: (
      state,
      action: PayloadAction<CreateLoadBalancerParams>
    ) => {
      state.isLoadBalancerCreating = true;
    },
    createLoadBalancerSuccess: (
      state,
      action: PayloadAction<CreateLoadBalancerResponse>
    ) => {
      state.isLoadBalancerCreating = false;
    },
    createLoadBalancerFailure: (
      state,
      action: PayloadAction<{ params: CreateLoadBalancerParams; error: string }>
    ) => {
      state.isLoadBalancerCreating = false;
    },

    updateLoadBalancerRequest: (
      state,
      action: PayloadAction<UpdateLoadBalancerParams>
    ) => {
      state.isLoadBalancerUpdating = true;
    },
    updateLoadBalancerSuccess: (
      state,
      action: PayloadAction<UpdateLoadBalancerResponse>
    ) => {
      state.isLoadBalancerUpdating = false;
    },
    updateLoadBalancerFailure: (
      state,
      action: PayloadAction<{ params: UpdateLoadBalancerParams; error: string }>
    ) => {
      state.isLoadBalancerUpdating = false;
    },

    deleteLoadBalancerRequest: (
      state,
      action: PayloadAction<DeleteLoadBalancerParams>
    ) => {
      state.isLoadBalancerDeleting = true;
    },
    deleteLoadBalancerSuccess: (state) => {
      state.isLoadBalancerDeleting = false;
      state.loadBalancer = null;
    },
    deleteLoadBalancerFailure: (
      state,
      action: PayloadAction<{ params: DeleteLoadBalancerParams; error: string }>
    ) => {
      state.isLoadBalancerDeleting = false;
    },

    // Listeners
    listListenersRequest: (
      state,
      action: PayloadAction<GetListenersListParams>
    ) => {
      state.areListenersLoading = true;
    },
    listListenersSuccess: (
      state,
      action: PayloadAction<GetListenersListResponse>
    ) => {
      state.listeners = action.payload;
      state.areListenersLoading = false;
    },
    listListenersFailure: (
      state,
      action: PayloadAction<{ params: GetListenersListParams; error: string }>
    ) => {
      state.areListenersLoading = false;
    },

    getListenerRequest: (
      state,
      action: PayloadAction<GetListenerDetailsParams>
    ) => {
      state.isListenerLoading = true;
    },
    getListenerSuccess: (
      state,
      action: PayloadAction<GetListenerDetailsResponse>
    ) => {
      state.listener = action.payload;
      state.isListenerLoading = false;
    },
    getListenerFailure: (
      state,
      action: PayloadAction<{ params: GetListenerDetailsParams; error: string }>
    ) => {
      state.isListenerLoading = false;
    },

    createListenerRequest: (
      state,
      action: PayloadAction<CreateListenerParams>
    ) => {
      state.isListenerCreating = true;
    },
    createListenerSuccess: (
      state,
      action: PayloadAction<CreateListenerResponse>
    ) => {
      state.isListenerCreating = false;
    },
    createListenerFailure: (
      state,
      action: PayloadAction<{ params: CreateListenerParams; error: string }>
    ) => {
      state.isListenerCreating = false;
    },

    updateListenerRequest: (
      state,
      action: PayloadAction<UpdateListenerParams>
    ) => {
      state.isListenerUpdating = true;
    },
    updateListenerSuccess: (
      state,
      action: PayloadAction<UpdateListenerResponse>
    ) => {
      state.isListenerUpdating = false;
    },
    updateListenerFailure: (
      state,
      action: PayloadAction<{ params: UpdateListenerParams; error: string }>
    ) => {
      state.isListenerUpdating = false;
    },

    deleteListenerRequest: (
      state,
      action: PayloadAction<DeleteListenerParams>
    ) => {
      state.isListenerDeleting = true;
    },
    deleteListenerSuccess: (state) => {
      state.isListenerDeleting = false;
      state.listener = null;
    },
    deleteListenerFailure: (
      state,
      action: PayloadAction<{ params: DeleteListenerParams; error: string }>
    ) => {
      state.isListenerDeleting = false;
    },

    // Pools
    listPoolsRequest: (state, action: PayloadAction<GetPoolsListParams>) => {
      state.arePoolsLoading = true;
    },
    listPoolsSuccess: (state, action: PayloadAction<GetPoolsListResponse>) => {
      state.pools = action.payload;
      state.arePoolsLoading = false;
    },
    listPoolsFailure: (
      state,
      action: PayloadAction<{ params: GetPoolsListParams; error: string }>
    ) => {
      state.arePoolsLoading = false;
    },

    getPoolRequest: (state, action: PayloadAction<GetPoolDetailsParams>) => {
      state.isPoolLoading = true;
    },
    getPoolSuccess: (state, action: PayloadAction<GetPoolDetailsResponse>) => {
      state.pool = action.payload;
      state.isPoolLoading = false;
    },
    getPoolFailure: (
      state,
      action: PayloadAction<{ params: GetPoolDetailsParams; error: string }>
    ) => {
      state.isPoolLoading = false;
    },

    createPoolRequest: (state, action: PayloadAction<CreatePoolParams>) => {
      state.isPoolCreating = true;
    },
    createPoolSuccess: (state, action: PayloadAction<CreatePoolResponse>) => {
      state.isPoolCreating = false;
    },
    createPoolFailure: (
      state,
      action: PayloadAction<{ params: CreatePoolParams; error: string }>
    ) => {
      state.isPoolCreating = false;
    },

    updatePoolRequest: (state, action: PayloadAction<UpdatePoolParams>) => {
      state.isPoolUpdating = true;
    },
    updatePoolSuccess: (state, action: PayloadAction<UpdatePoolResponse>) => {
      state.isPoolUpdating = false;
    },
    updatePoolFailure: (
      state,
      action: PayloadAction<{ params: UpdatePoolParams; error: string }>
    ) => {
      state.isPoolUpdating = false;
    },

    deletePoolRequest: (state, action: PayloadAction<DeletePoolParams>) => {
      state.isPoolDeleting = true;
    },
    deletePoolSuccess: (state) => {
      state.isPoolDeleting = false;
      state.pool = null;
    },
    deletePoolFailure: (
      state,
      action: PayloadAction<{ params: DeletePoolParams; error: string }>
    ) => {
      state.isPoolDeleting = false;
    },

    // Pool Members
    listPoolMembersRequest: (
      state,
      action: PayloadAction<GetPoolMembersListParams>
    ) => {
      state.arePoolMembersLoading = true;
    },
    listPoolMembersSuccess: (
      state,
      action: PayloadAction<GetPoolMembersListResponse>
    ) => {
      state.poolMembers = action.payload;
      state.arePoolMembersLoading = false;
    },
    listPoolMembersFailure: (
      state,
      action: PayloadAction<{ params: GetPoolMembersListParams; error: string }>
    ) => {
      state.arePoolMembersLoading = false;
    },

    getPoolMemberRequest: (
      state,
      action: PayloadAction<GetPoolMemberDetailsParams>
    ) => {
      state.isPoolMemberLoading = true;
    },
    getPoolMemberSuccess: (
      state,
      action: PayloadAction<GetPoolMemberDetailsResponse>
    ) => {
      state.poolMember = action.payload;
      state.isPoolMemberLoading = false;
    },
    getPoolMemberFailure: (
      state,
      action: PayloadAction<{
        params: GetPoolMemberDetailsParams;
        error: string;
      }>
    ) => {
      state.isPoolMemberLoading = false;
    },

    addPoolMemberRequest: (
      state,
      action: PayloadAction<AddPoolMemberParams>
    ) => {
      state.isPoolMemberAdding = true;
    },
    addPoolMemberSuccess: (
      state,
      action: PayloadAction<AddPoolMemberResponse>
    ) => {
      state.isPoolMemberAdding = false;
    },
    addPoolMemberFailure: (
      state,
      action: PayloadAction<{ params: AddPoolMemberParams; error: string }>
    ) => {
      state.isPoolMemberAdding = false;
    },

    updatePoolMemberRequest: (
      state,
      action: PayloadAction<UpdatePoolMemberParams>
    ) => {
      state.isPoolMemberUpdating = true;
    },
    updatePoolMemberSuccess: (
      state,
      action: PayloadAction<UpdatePoolMemberResponse>
    ) => {
      state.isPoolMemberUpdating = false;
    },
    updatePoolMemberFailure: (
      state,
      action: PayloadAction<{ params: UpdatePoolMemberParams; error: string }>
    ) => {
      state.isPoolMemberUpdating = false;
    },

    removePoolMemberRequest: (
      state,
      action: PayloadAction<RemovePoolMemberParams>
    ) => {
      state.isPoolMemberRemoving = true;
    },
    removePoolMemberSuccess: (state) => {
      state.isPoolMemberRemoving = false;
      state.poolMember = null;
    },
    removePoolMemberFailure: (
      state,
      action: PayloadAction<{ params: RemovePoolMemberParams; error: string }>
    ) => {
      state.isPoolMemberRemoving = false;
    },

    // L7 Policies
    listL7PoliciesRequest: (
      state,
      action: PayloadAction<GetL7PoliciesListParams>
    ) => {
      state.areL7PoliciesLoading = true;
    },
    listL7PoliciesSuccess: (
      state,
      action: PayloadAction<GetL7PoliciesListResponse>
    ) => {
      state.l7Policies = action.payload;
      state.areL7PoliciesLoading = false;
    },
    listL7PoliciesFailure: (
      state,
      action: PayloadAction<{ params: GetL7PoliciesListParams; error: string }>
    ) => {
      state.areL7PoliciesLoading = false;
    },

    getL7PolicyRequest: (
      state,
      action: PayloadAction<GetL7PolicyDetailsParams>
    ) => {
      state.isL7PolicyLoading = true;
    },
    getL7PolicySuccess: (
      state,
      action: PayloadAction<GetL7PolicyDetailsResponse>
    ) => {
      state.l7Policy = action.payload;
      state.isL7PolicyLoading = false;
    },
    getL7PolicyFailure: (
      state,
      action: PayloadAction<{ params: GetL7PolicyDetailsParams; error: string }>
    ) => {
      state.isL7PolicyLoading = false;
    },

    createL7PolicyRequest: (
      state,
      action: PayloadAction<CreateL7PolicyParams>
    ) => {
      state.isL7PolicyCreating = true;
    },
    createL7PolicySuccess: (
      state,
      action: PayloadAction<CreateL7PolicyResponse>
    ) => {
      state.isL7PolicyCreating = false;
    },
    createL7PolicyFailure: (
      state,
      action: PayloadAction<{ params: CreateL7PolicyParams; error: string }>
    ) => {
      state.isL7PolicyCreating = false;
    },

    updateL7PolicyRequest: (
      state,
      action: PayloadAction<UpdateL7PolicyParams>
    ) => {
      state.isL7PolicyUpdating = true;
    },
    updateL7PolicySuccess: (
      state,
      action: PayloadAction<UpdateL7PolicyResponse>
    ) => {
      state.isL7PolicyUpdating = false;
    },
    updateL7PolicyFailure: (
      state,
      action: PayloadAction<{ params: UpdateL7PolicyParams; error: string }>
    ) => {
      state.isL7PolicyUpdating = false;
    },

    deleteL7PolicyRequest: (
      state,
      action: PayloadAction<DeleteL7PolicyParams>
    ) => {
      state.isL7PolicyDeleting = true;
    },
    deleteL7PolicySuccess: (state) => {
      state.isL7PolicyDeleting = false;
      state.l7Policy = null;
    },
    deleteL7PolicyFailure: (
      state,
      action: PayloadAction<{ params: DeleteL7PolicyParams; error: string }>
    ) => {
      state.isL7PolicyDeleting = false;
    },

    // L7 Policy Rules
    listL7PolicyRulesRequest: (
      state,
      action: PayloadAction<GetL7RulesListParams>
    ) => {
      state.areL7PolicyRulesLoading = true;
    },
    listL7PolicyRulesSuccess: (
      state,
      action: PayloadAction<GetL7RulesListResponse>
    ) => {
      state.l7PolicyRules = action.payload;
      state.areL7PolicyRulesLoading = false;
    },
    listL7PolicyRulesFailure: (
      state,
      action: PayloadAction<{ params: GetL7RulesListParams; error: string }>
    ) => {
      state.areL7PolicyRulesLoading = false;
    },

    getL7PolicyRuleRequest: (
      state,
      action: PayloadAction<GetL7RuleDetailsParams>
    ) => {
      state.isL7PolicyRuleLoading = true;
    },
    getL7PolicyRuleSuccess: (
      state,
      action: PayloadAction<GetL7RuleDetailsResponse>
    ) => {
      state.l7PolicyRule = action.payload;
      state.isL7PolicyRuleLoading = false;
    },
    getL7PolicyRuleFailure: (
      state,
      action: PayloadAction<{ params: GetL7RuleDetailsParams; error: string }>
    ) => {
      state.isL7PolicyRuleLoading = false;
    },

    createL7PolicyRuleRequest: (
      state,
      action: PayloadAction<CreateL7RuleParams>
    ) => {
      state.isL7PolicyRuleCreating = true;
    },
    createL7PolicyRuleSuccess: (
      state,
      action: PayloadAction<CreateL7RuleResponse>
    ) => {
      state.isL7PolicyRuleCreating = false;
    },
    createL7PolicyRuleFailure: (
      state,
      action: PayloadAction<{ params: CreateL7RuleParams; error: string }>
    ) => {
      state.isL7PolicyRuleCreating = false;
    },

    updateL7PolicyRuleRequest: (
      state,
      action: PayloadAction<UpdateL7RuleParams>
    ) => {
      state.isL7PolicyRuleUpdating = true;
    },
    updateL7PolicyRuleSuccess: (
      state,
      action: PayloadAction<UpdateL7RuleResponse>
    ) => {
      state.isL7PolicyRuleUpdating = false;
    },
    updateL7PolicyRuleFailure: (
      state,
      action: PayloadAction<{ params: UpdateL7RuleParams; error: string }>
    ) => {
      state.isL7PolicyRuleUpdating = false;
    },

    deleteL7PolicyRuleRequest: (
      state,
      action: PayloadAction<DeleteL7RuleParams>
    ) => {
      state.isL7PolicyRuleDeleting = true;
    },
    deleteL7PolicyRuleSuccess: (state) => {
      state.isL7PolicyRuleDeleting = false;
      state.l7PolicyRule = null;
    },
    deleteL7PolicyRuleFailure: (
      state,
      action: PayloadAction<{ params: DeleteL7RuleParams; error: string }>
    ) => {
      state.isL7PolicyRuleDeleting = false;
    },

    // Health Monitors
    listHealthMonitorsRequest: (
      state,
      action: PayloadAction<GetHealthMonitorsListParams>
    ) => {
      state.areHealthMonitorsLoading = true;
    },
    listHealthMonitorsSuccess: (
      state,
      action: PayloadAction<GetHealthMonitorsListResponse>
    ) => {
      state.healthMonitors = action.payload;
      state.areHealthMonitorsLoading = false;
    },
    listHealthMonitorsFailure: (
      state,
      action: PayloadAction<{
        params: GetHealthMonitorsListParams;
        error: string;
      }>
    ) => {
      state.areHealthMonitorsLoading = false;
    },

    getHealthMonitorRequest: (
      state,
      action: PayloadAction<GetHealthMonitorDetailsParams>
    ) => {
      state.isHealthMonitorLoading = true;
    },
    getHealthMonitorSuccess: (
      state,
      action: PayloadAction<GetHealthMonitorDetailsResponse>
    ) => {
      state.healthMonitor = action.payload;
      state.isHealthMonitorLoading = false;
    },
    getHealthMonitorFailure: (
      state,
      action: PayloadAction<{
        params: GetHealthMonitorDetailsParams;
        error: string;
      }>
    ) => {
      state.isHealthMonitorLoading = false;
    },

    createHealthMonitorRequest: (
      state,
      action: PayloadAction<CreateHealthMonitorParams>
    ) => {
      state.isHealthMonitorCreating = true;
    },
    createHealthMonitorSuccess: (
      state,
      action: PayloadAction<CreateHealthMonitorResponse>
    ) => {
      state.isHealthMonitorCreating = false;
    },
    createHealthMonitorFailure: (
      state,
      action: PayloadAction<{
        params: CreateHealthMonitorParams;
        error: string;
      }>
    ) => {
      state.isHealthMonitorCreating = false;
    },

    updateHealthMonitorRequest: (
      state,
      action: PayloadAction<UpdateHealthMonitorParams>
    ) => {
      state.isHealthMonitorUpdating = true;
    },
    updateHealthMonitorSuccess: (
      state,
      action: PayloadAction<UpdateHealthMonitorResponse>
    ) => {
      state.isHealthMonitorUpdating = false;
    },
    updateHealthMonitorFailure: (
      state,
      action: PayloadAction<{
        params: UpdateHealthMonitorParams;
        error: string;
      }>
    ) => {
      state.isHealthMonitorUpdating = false;
    },

    deleteHealthMonitorRequest: (
      state,
      action: PayloadAction<DeleteHealthMonitorParams>
    ) => {
      state.isHealthMonitorDeleting = true;
    },
    deleteHealthMonitorSuccess: (state) => {
      state.isHealthMonitorDeleting = false;
      state.healthMonitor = null;
    },
    deleteHealthMonitorFailure: (
      state,
      action: PayloadAction<{
        params: DeleteHealthMonitorParams;
        error: string;
      }>
    ) => {
      state.isHealthMonitorDeleting = false;
    },

    // Clear helpers
    clearPoolsList: (state) => {
      state.pools = null;
    },
    clearListenersList: (state) => {
      state.listeners = null;
    },
    clearL7PoliciesList: (state) => {
      state.l7Policies = null;
    },
    clearMonitorsList: (state) => {
      state.healthMonitors = null;
    },
    clearMonitorDetails: (state) => {
      state.healthMonitor = null;
    },
    clearPoolDetails: (state) => {
      state.healthMonitor = null;
      state.pool = null;
      state.poolMembers = null;
      state.isPoolLoading = false;
    },
    clearPoolMembersList: (state) => {
      state.poolMembers = null;
    },
    clear: () => initialState
  }
});

export const {
  listLoadBalancersRequest,
  listLoadBalancersSuccess,
  listLoadBalancersFailure,
  getLoadBalancerRequest,
  getLoadBalancerSuccess,
  getLoadBalancerFailure,
  createLoadBalancerRequest,
  createLoadBalancerSuccess,
  createLoadBalancerFailure,
  updateLoadBalancerRequest,
  updateLoadBalancerSuccess,
  updateLoadBalancerFailure,
  deleteLoadBalancerRequest,
  deleteLoadBalancerSuccess,
  deleteLoadBalancerFailure,
  listListenersRequest,
  listListenersSuccess,
  listListenersFailure,
  getListenerRequest,
  getListenerSuccess,
  getListenerFailure,
  createListenerRequest,
  createListenerSuccess,
  createListenerFailure,
  updateListenerRequest,
  updateListenerSuccess,
  updateListenerFailure,
  deleteListenerRequest,
  deleteListenerSuccess,
  deleteListenerFailure,
  listPoolsRequest,
  listPoolsSuccess,
  listPoolsFailure,
  getPoolRequest,
  getPoolSuccess,
  getPoolFailure,
  createPoolRequest,
  createPoolSuccess,
  createPoolFailure,
  updatePoolRequest,
  updatePoolSuccess,
  updatePoolFailure,
  deletePoolRequest,
  deletePoolSuccess,
  deletePoolFailure,
  listPoolMembersRequest,
  listPoolMembersSuccess,
  listPoolMembersFailure,
  getPoolMemberRequest,
  getPoolMemberSuccess,
  getPoolMemberFailure,
  addPoolMemberRequest,
  addPoolMemberSuccess,
  addPoolMemberFailure,
  updatePoolMemberRequest,
  updatePoolMemberSuccess,
  updatePoolMemberFailure,
  removePoolMemberRequest,
  removePoolMemberSuccess,
  removePoolMemberFailure,
  listL7PoliciesRequest,
  listL7PoliciesSuccess,
  listL7PoliciesFailure,
  getL7PolicyRequest,
  getL7PolicySuccess,
  getL7PolicyFailure,
  createL7PolicyRequest,
  createL7PolicySuccess,
  createL7PolicyFailure,
  updateL7PolicyRequest,
  updateL7PolicySuccess,
  updateL7PolicyFailure,
  deleteL7PolicyRequest,
  deleteL7PolicySuccess,
  deleteL7PolicyFailure,
  listL7PolicyRulesRequest,
  listL7PolicyRulesSuccess,
  listL7PolicyRulesFailure,
  getL7PolicyRuleRequest,
  getL7PolicyRuleSuccess,
  getL7PolicyRuleFailure,
  createL7PolicyRuleRequest,
  createL7PolicyRuleSuccess,
  createL7PolicyRuleFailure,
  updateL7PolicyRuleRequest,
  updateL7PolicyRuleSuccess,
  updateL7PolicyRuleFailure,
  deleteL7PolicyRuleRequest,
  deleteL7PolicyRuleSuccess,
  deleteL7PolicyRuleFailure,
  listHealthMonitorsRequest,
  listHealthMonitorsSuccess,
  listHealthMonitorsFailure,
  getHealthMonitorRequest,
  getHealthMonitorSuccess,
  getHealthMonitorFailure,
  createHealthMonitorRequest,
  createHealthMonitorSuccess,
  createHealthMonitorFailure,
  updateHealthMonitorRequest,
  updateHealthMonitorSuccess,
  updateHealthMonitorFailure,
  deleteHealthMonitorRequest,
  deleteHealthMonitorSuccess,
  deleteHealthMonitorFailure,
  clearPoolsList,
  clearListenersList,
  clearL7PoliciesList,
  clearMonitorsList,
  clearMonitorDetails,
  clearPoolDetails,
  clearPoolMembersList,
  clear
} = loadBalancersSlice.actions;

export const { actions: loadBalancersActions } = loadBalancersSlice;

export const reducer = loadBalancersSlice.reducer;
